/**
 * Ce que le magasin doit préparer, et le geste qui sort le stock.
 *
 * *Demande de Chris (`docs/dev_stock.docx`) : « [le magasinier] devra valider qu'il a bien préparé
 * la commande, donc il doit avoir un environnement où il valide la préparation pour signifier à
 * l'assistante que c'est ok ».* Et, du 07/09/2026 : **une préparation validée SORT le stock**.
 *
 * ## Ce qui se lit sans cliquer
 *
 * **L'affaire et le chantier**, parce que c'est pour eux qu'on prépare — c'est le seul endroit du
 * produit où le magasinier voit un chantier, et il n'en voit rien d'autre : ni montant, ni
 * planning. La règle du relais qui rétrécit.
 *
 * **Pour quand**, et en retard le cas échéant. Une date passée est la seule chose qui distingue
 * une préparation à faire d'une préparation qui aurait dû être faite.
 *
 * **Ce qui est déjà préparé** reste affiché en dessous, replié : c'est là qu'on va voir si l'on
 * a bien sorti ce qu'on croit avoir sorti — et le geste de correction, lui, est sur le mouvement
 * de stock, pas ici.
 *
 * ## Le bouton dit ce qu'il fait
 *
 * « J'ai préparé » sortirait le stock sans le dire. Valider une préparation **retire la
 * marchandise du magasin** : le bouton le nomme, parce que c'est un geste qui déplace de la
 * valeur et qu'on ne défait pas d'un clic.
 */
import { useCallback, useEffect, useState } from "react";
import type { JSX } from "react";
import { chargerLesPreparations, marquerPreparationFaite } from "./api";
import type { Reservation } from "./api";

function formaterDate(iso: string): string {
  const d = new Date(iso);
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${String(d.getFullYear())}`;
}

/** Une date civile « 2026-09-15 » se lit « 15/09/2026 », sans passer par un objet date. */
function formaterDateCivile(iso: string): string {
  const [a, m, j] = iso.split("-");
  return `${j ?? ""}/${m ?? ""}/${a ?? ""}`;
}

function sansZeros(valeur: string): string {
  return valeur.includes(".") ? valeur.replace(/\.?0+$/, "") : valeur;
}

/**
 * Aujourd'hui, en date civile.
 *
 * Comparée en CHAÎNES, jamais par un objet date : c'est la convention du calendrier du produit,
 * et c'est ce qui rend impossible le décalage de fuseau qui avait fait compter 254 jours ouvrés
 * au lieu de 252 sur bien-archiver.
 */
function aujourdHui(): string {
  const d = new Date();
  return `${String(d.getFullYear())}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export function Preparations({
  onOuvrirArticle,
}: {
  /** Ouvrir la fiche de stock de l'article — pour vérifier ce qu'il reste après la sortie. */
  readonly onOuvrirArticle?: (articleId: string) => void;
}): JSX.Element {
  const [preparations, setPreparations] = useState<readonly Reservation[] | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [avis, setAvis] = useState<string | null>(null);
  const [occupe, setOccupe] = useState<string | null>(null);
  const [voirFaites, setVoirFaites] = useState(false);

  const recharger = useCallback((): void => {
    chargerLesPreparations()
      .then((r) => {
        setPreparations(r.preparations);
        setErreur(null);
      })
      .catch((x: unknown) => {
        setErreur(x instanceof Error ? x.message : String(x));
      });
  }, []);

  useEffect(() => {
    recharger();
  }, [recharger]);

  const preparer = (r: Reservation): void => {
    setOccupe(r.id);
    setErreur(null);
    setAvis(null);
    marquerPreparationFaite(r.id)
      .then((resultat) => {
        // Le stock qui RESTE après la sortie : c'est ce que le magasinier vérifie du regard
        // devant son rayonnage, et le seul moyen d'attraper une erreur de quantité tout de suite.
        setAvis(
          `${sansZeros(r.quantite)} ${r.unite ?? ""} sortis pour ${r.apo}. ` +
            `Il reste ${sansZeros(resultat.etat.quantite)} en stock.` +
            (resultat.etat.negatif
              ? " ATTENTION : le stock est passé sous zéro, il manque une entrée."
              : ""),
        );
        recharger();
      })
      .catch((x: unknown) => {
        setErreur(x instanceof Error ? x.message : String(x));
      })
      .finally(() => {
        setOccupe(null);
      });
  };

  const toutes = preparations ?? [];
  const aFaire = toutes.filter((r) => r.statut === "demandee");
  const faites = toutes.filter((r) => r.statut === "preparee");
  const jour = aujourdHui();

  return (
    <section className="preparations">
      <h3>
        Préparations à faire
        {aFaire.length > 0 && <span className="preparations__compte">{aFaire.length}</span>}
      </h3>

      {erreur !== null && (
        <p className="alerte" role="alert">
          {erreur}
        </p>
      )}
      {avis !== null && <p className="etat">{avis}</p>}

      {preparations === null ? (
        <p className="etat">Chargement…</p>
      ) : aFaire.length === 0 ? (
        <p className="etat">Rien à préparer.</p>
      ) : (
        <ul className="preparations__liste">
          {aFaire.map((r) => {
            const enRetard = r.pourLe !== null && r.pourLe < jour;
            return (
              <li
                key={r.id}
                className={enRetard ? "preparations__ligne en-retard" : "preparations__ligne"}
              >
                <p className="preparations__entete">
                  <strong>
                    {sansZeros(r.quantite)} {r.unite ?? ""}
                  </strong>
                  <span className="preparations__article">
                    {r.articleCode} — {r.articleDescription}
                  </span>
                </p>

                <p className="preparations__details">
                  {/* Pour QUI on prépare : la seule chose du chantier que ce compte voie. */}
                  <span className="preparations__apo">
                    {r.apo} · {r.nomChantier}
                  </span>
                  {r.pourLe !== null && (
                    <span className={enRetard ? "en-ecart" : undefined}>
                      pour le {formaterDateCivile(r.pourLe)}
                      {enRetard && " — en retard"}
                    </span>
                  )}
                  {r.pourLe === null && <span>aucune date demandée</span>}
                  {/*
                    DANS QUEL MAGASIN préparer (078).

                    Toujours affiché, même avec un seul magasin : c'est l'information qui dit où
                    aller, et le second compte annoncé (l'atelier) recevra ces mêmes lignes sans
                    qu'on repasse par cet écran. Une réservation d'avant la 078 n'en porte pas —
                    on le dit plutôt que de laisser un blanc que le magasinier lirait « chez moi ».
                  */}
                  <span className="preparations__magasin">
                    {r.siteDenomination ?? "magasin non désigné"}
                  </span>
                  <span>
                    demandé le {formaterDate(r.demandeLe)}
                    {r.demandeurNom !== null && ` par ${r.demandeurNom}`}
                  </span>
                </p>

                {r.remarque !== null && (
                  <p className="preparations__remarque">« {r.remarque} »</p>
                )}

                {/* Le bouton nomme sa conséquence : valider RETIRE la marchandise du stock. */}
                <button
                  type="button"
                  disabled={occupe !== null}
                  onClick={() => {
                    preparer(r);
                  }}
                >
                  {occupe === r.id
                    ? "Enregistrement…"
                    : `Préparé — sortir ${sansZeros(r.quantite)} du stock`}
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {faites.length > 0 && (
        <details
          className="preparations__faites"
          open={voirFaites}
          onToggle={(e) => {
            setVoirFaites((e.currentTarget as HTMLDetailsElement).open);
          }}
        >
          <summary>Déjà préparé ({faites.length})</summary>
          <ul>
            {faites.map((r) => (
              <li key={r.id}>
                <span>
                  {sansZeros(r.quantite)} {r.unite ?? ""} · {r.articleCode} · {r.apo}
                </span>
                <span className="preparations__quand">
                  {r.prepareLe === null ? "" : formaterDate(r.prepareLe)}
                  {r.preparateurNom !== null && ` · ${r.preparateurNom}`}
                </span>
                {/* Une préparation dont la sortie a été supprimée : à dire, jamais à taire. Le
                    stock ne porte plus cette sortie, et personne ne le devinerait. */}
                {r.mouvementId === null && (
                  <strong className="en-ecart">sortie de stock supprimée</strong>
                )}
                {onOuvrirArticle !== undefined && (
                  <button
                    type="button"
                    className="lien"
                    onClick={() => {
                      onOuvrirArticle(r.articleId);
                    }}
                  >
                    Ouvrir la fiche
                  </button>
                )}
              </li>
            ))}
          </ul>
        </details>
      )}
    </section>
  );
}
